import { useEffect, useCallback, useRef } from 'react';

interface UsePageFocusOptions {
  onFocus?: () => void | Promise<void>;
  debounceMs?: number; // 防止事件風暴，默認 120ms
  enabled?: boolean; // 是否啟用，默認 true
}

/**
 * 通用的頁面焦點恢復 Hook
 * 解決 tab 切換後功能失效的問題
 * 
 * 重點：
 * 1. 同時監聽 visibilitychange + focus
 * 2. 回到可見時立刻執行 refetch
 * 3. 避免「早退」陷阱
 * 4. 事件風暴去抖
 */
export const usePageFocus = (options: UsePageFocusOptions = {}) => {
  const {
    onFocus,
    debounceMs = 120, // 120ms 合併 focus/visibility 連擊
    enabled = true
  } = options;

  // 穩定的 onFocus 引用，避免重複綁定事件
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;

  // 去抖計時器 - 使用 number 類型以支持 window.setTimeout
  const tickingRef = useRef<number | null>(null);
  // 最後觸發時間，避免重複觸發
  const lastTriggeredRef = useRef<number>(0);
  // 跟踪頁面是否曾經被隱藏，用於避免初始載入時的pageshow觸發
  const wasHiddenRef = useRef<boolean>(false);
  // 跟踪是否已經處理過初始的pageshow事件，用於處理應用內歷史導航
  // 當 hook 在文檔完全載入後掛載時初始化為 true，避免歷史導航被視為初始載入
  const hasHandledInitialPageShowRef = useRef<boolean>(
    typeof document !== 'undefined' && document.readyState === 'complete'
  );

  /**
   * 防抖的焦點恢復處理器 - 加強重入保護
   */
  const debouncedOnFocus = useCallback(async () => {
    if (!enabled || !onFocusRef.current) return;

    // 重入保護 - 如果已經有待執行的任務，跳過
    if (tickingRef.current !== null) {
      console.log('🔄 頁面焦點恢復被重入保護跳過');
      return;
    }

    tickingRef.current = window.setTimeout(() => {
      // 立即重置 tickingRef，防止異步操作掛起時阻塞後續事件
      tickingRef.current = null;

      const now = Date.now();

      // 避免在短時間內重複觸發（120ms 內）
      if (now - lastTriggeredRef.current < 120) {
        console.log('🔄 頁面焦點恢復被去抖跳過');
        return;
      }

      lastTriggeredRef.current = now;
      console.log('🔄 頁面焦點恢復觸發');

      // 先存下當前的 handler，避免在異步執行期間引用變化
      const handler = onFocusRef.current;

      // 使用 Promise.resolve() 非阻塞地處理異步操作
      if (handler) {
        Promise.resolve(handler())
          .catch((error) => {
            console.error('🚫 頁面焦點恢復執行錯誤:', error);
          });
      }

      // 立即重置wasHidden標記，因為我們已經處理了這個恢復事件
      wasHiddenRef.current = false;
    }, debounceMs);
  }, [enabled, debounceMs]);

  /**
   * 處理 visibilitychange 事件
   */
  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden) {
      console.log('📡 visibilitychange: 頁面變為可見');
      debouncedOnFocus();
    } else {
      console.log('📡 visibilitychange: 頁面變為隱藏');
      wasHiddenRef.current = true; // 標記頁面曾經被隱藏
    }
  }, [debouncedOnFocus]);

  /**
   * 處理 window focus 事件
   */
  const handleWindowFocus = useCallback(() => {
    console.log('📡 window focus: 視窗獲得焦點');
    debouncedOnFocus();
  }, [debouncedOnFocus]);

  /**
   * 處理 window blur 事件
   */
  const handleWindowBlur = useCallback(() => {
    console.log('📡 window blur: 視窗失去焦點');
    wasHiddenRef.current = true; // 標記頁面曾經失去焦點
    // 清除待執行的恢復操作
    if (tickingRef.current !== null) {
      clearTimeout(tickingRef.current);
      tickingRef.current = null;
    }
  }, []);

  /**
   * 處理 pageshow 事件 - 處理從 BFCache 恢復、歷史導航或其他返回頁面情況
   */
  const handlePageShow = useCallback((event: PageTransitionEvent) => {
    // 在以下情況觸發恢復：
    // 1. 從 BFCache 恢復 (event.persisted = true)
    // 2. 頁面曾經被隱藏過 (wasHiddenRef.current = true)
    // 3. 已經處理過初始 pageshow 且頁面可見 (處理應用內歷史導航)
    const shouldTriggerRecovery = event.persisted ||
                                 wasHiddenRef.current ||
                                 (document.visibilityState === 'visible' && hasHandledInitialPageShowRef.current);

    if (shouldTriggerRecovery) {
      const reason = event.persisted ? '從 BFCache 恢復' :
                    wasHiddenRef.current ? '頁面從隱藏狀態返回' :
                    '應用內歷史導航';
      console.log(`📡 pageshow: ${reason}頁面`);
      debouncedOnFocus();
    } else {
      console.log('📡 pageshow: 忽略初始頁面載入');
    }

    // 標記已經處理過 pageshow 事件
    hasHandledInitialPageShowRef.current = true;
  }, [debouncedOnFocus]);

  /**
   * 手動觸發焦點恢復（用於強制刷新）
   */
  const triggerFocusRecovery = useCallback(() => {
    console.log('🔄 手動觸發頁面焦點恢復');
    if (onFocusRef.current) {
      onFocusRef.current();
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    console.log('🎯 啟用頁面焦點恢復監聽');

    // 同時監聽 visibilitychange + focus + pageshow (BFCache)
    // 不同作業系統/多視窗下，有時只觸發其一
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('pageshow', handlePageShow);

    // 清理函數
    return () => {
      console.log('🧹 清理頁面焦點恢復監聽');

      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('pageshow', handlePageShow);
      
      // 清理去抖計時器
      if (tickingRef.current !== null) {
        clearTimeout(tickingRef.current);
        tickingRef.current = null;
      }
    };
  }, [enabled]); // 移除回調函數依賴，避免重複綁定

  return {
    triggerFocusRecovery
  };
};

export default usePageFocus;
